import { Table } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconSelector } from '@tabler/icons-react';
import { useMemo, useState } from 'react';

import { paymentTypes } from '../../types';

import { BodyRow } from './BodyRow';

import { parsePaymentDate } from '@/utils/dates';

type PaymentsTableProps = {
  payments: paymentTypes.Payment[];
  currencySearch: string;
  clientSearch: string;
  paymentMethodSearch: string;
  dateFrom: Date | null;
  dateTo: Date | null;
};

export const PaymentsTable = ({
  payments,
  currencySearch,
  clientSearch,
  paymentMethodSearch,
  dateFrom,
  dateTo,
}: PaymentsTableProps) => {
  type SortKey = 'amount' | 'currency' | 'client_details' | 'payment_method' | 'date' | 'source';
  type SortDirection = 'asc' | 'desc';

  const [sortBy, setSortBy] = useState<SortKey>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDirection('asc');
    }
  };

  const filteredPayments = payments.filter((payment) => {
    const matchesCurrency = payment.currency.toLowerCase().includes(currencySearch.toLowerCase());
    const matchesClient = payment.client_details.toLowerCase().includes(clientSearch.toLowerCase());
    const matchesPaymentMethod = payment.payment_method
      .toLowerCase()
      .includes(paymentMethodSearch.toLowerCase());

    // Check date range
    const paymentDate = parsePaymentDate(payment.date);
    if (!paymentDate) return false;

    const paymentDateOnly = new Date(paymentDate);
    paymentDateOnly.setHours(0, 0, 0, 0);

    const dateFromOnly = dateFrom
      ? (() => {
          const d = new Date(dateFrom);
          d.setHours(0, 0, 0, 0);
          return d;
        })()
      : null;

    const dateToOnly = dateTo
      ? (() => {
          const d = new Date(dateTo);
          d.setHours(0, 0, 0, 0);
          return d;
        })()
      : null;

    const matchesDateFrom = !dateFromOnly || paymentDateOnly >= dateFromOnly;
    const matchesDateTo = !dateToOnly || paymentDateOnly <= dateToOnly;

    return (
      matchesCurrency && matchesClient && matchesPaymentMethod && matchesDateFrom && matchesDateTo
    );
  });

  const sortedPayments = useMemo(() => {
    const data = [...filteredPayments];
    data.sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;
      switch (sortBy) {
        case 'amount':
          return (a.amount - b.amount) * dir;
        case 'currency':
          return a.currency.localeCompare(b.currency) * dir;
        case 'client_details':
          return a.client_details.localeCompare(b.client_details) * dir;
        case 'payment_method':
          return a.payment_method.localeCompare(b.payment_method) * dir;
        case 'date': {
          const dateA = parsePaymentDate(a.date);
          const dateB = parsePaymentDate(b.date);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return (dateA.valueOf() - dateB.valueOf()) * dir;
        }
        case 'source':
          return a.source.localeCompare(b.source) * dir;
        default:
          return 0;
      }
    });
    return data;
  }, [filteredPayments, sortBy, sortDirection]);

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortBy !== column) return <IconSelector size={14} stroke={1.5} />;
    return sortDirection === 'asc' ? (
      <IconChevronUp size={14} stroke={1.5} />
    ) : (
      <IconChevronDown size={14} stroke={1.5} />
    );
  };

  return (
    <div
      style={{
        overflow: 'auto',
        height: '100%',
        width: '100%',
      }}
    >
      <Table stickyHeader striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th
              onClick={() => toggleSort('amount')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Amount <SortIcon column="amount" />
              </span>
            </Table.Th>
            <Table.Th
              onClick={() => toggleSort('currency')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Currency <SortIcon column="currency" />
              </span>
            </Table.Th>
            <Table.Th
              onClick={() => toggleSort('client_details')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Client <SortIcon column="client_details" />
              </span>
            </Table.Th>
            <Table.Th
              onClick={() => toggleSort('payment_method')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Payment Method <SortIcon column="payment_method" />
              </span>
            </Table.Th>
            <Table.Th
              onClick={() => toggleSort('date')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Date <SortIcon column="date" />
              </span>
            </Table.Th>
            <Table.Th
              onClick={() => toggleSort('source')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Source <SortIcon column="source" />
              </span>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sortedPayments?.map((payment) => <BodyRow key={payment._id} payment={payment} />)}
        </Table.Tbody>
      </Table>
    </div>
  );
};
